<template>
  <div class="interaction-card" :class="{ 'is-followup': isFollowUp, 'is-stuck': isStuck }">
    <div class="ic-main">
      <div class="ic-header">
        <span class="ic-name">
          {{ name || 'Sans titre' }}
          <span class="ic-type-badge" :class="typeClass">{{ typeLabel }}</span>
          <span v-if="isFollowUp" class="ic-followup-tag">↳ Follow-up #{{ interaction.turn_index }}</span>
        </span>
        <span class="ic-time">{{ formatTime(timeValue) }}</span>
      </div>

      <div v-if="email" class="ic-email">{{ email }}</div>

      <div v-if="interaction" class="ic-meta">
        <template v-if="mode === 'agent'">
          <span class="ic-metric">{{ interaction.n_turns || 0 }} tours</span>
          <span class="ic-sep">·</span>
          <span class="ic-metric">{{ interaction.n_tool_calls || 0 }} outils</span>
          <span v-if="toolsBreakdown" class="ic-tools">{{ toolsBreakdown }}</span>
        </template>
        <template v-else>
          <span v-if="interaction.n_points" class="ic-metric">{{ interaction.n_points }} points</span>
        </template>
        <template v-if="durationLabel">
          <span class="ic-sep">·</span>
          <span class="ic-metric">{{ durationLabel }}</span>
        </template>
        <template v-if="costLabel">
          <span class="ic-sep">·</span>
          <span class="ic-metric ic-cost">{{ costLabel }}</span>
        </template>
      </div>
    </div>

    <div class="ic-status">
      <span class="ic-status-badge" :class="statusClass">{{ statusLabel }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InteractionCard',
  props: {
    name: { type: String, default: '' },
    email: { type: String, default: '' },
    // interaction analytics (null = dossier legacy, avant la télémétrie)
    interaction: { type: Object, default: null },
    legacyMode: { type: String, default: '' },
    legacyTime: { type: String, default: '' },
    legacyAnalysing: { default: null },
  },
  computed: {
    isFollowUp() {
      return !!(this.interaction && this.interaction.turn_index > 0);
    },
    mode() {
      // mode analytics (minuscule) sinon legacy dossiers.MODE (FLASH/AGENT majuscule)
      const m = (this.interaction && this.interaction.mode) || this.legacyMode || '';
      return String(m).toLowerCase();
    },
    typeLabel() {
      return { flash: '⚡ Flash', agent: '✦ Agent', deep: '🔬 Deep' }[this.mode] || (this.mode || '—');
    },
    typeClass() {
      return 'type-' + (this.mode || 'unknown');
    },
    timeValue() {
      return (this.interaction && this.interaction.created_at) || this.legacyTime;
    },
    toolsBreakdown() {
      const t = this.interaction && this.interaction.tools_used;
      if (!t || typeof t !== 'object') return '';
      const parts = Object.entries(t)
        .filter(([, n]) => Number(n) > 0)
        .sort((a, b) => Number(b[1]) - Number(a[1]))
        .map(([tool, n]) => `${tool} ×${n}`);
      return parts.length ? '(' + parts.join(' · ') + ')' : '';
    },
    durationLabel() {
      const s = this.interaction && this.interaction.exec_seconds;
      if (s == null) return '';
      const sec = Math.round(Number(s));
      if (sec < 60) return `${sec}s`;
      return `${Math.floor(sec / 60)}m${String(sec % 60).padStart(2, '0')}`;
    },
    costLabel() {
      const c = this.interaction && this.interaction.cost_usd;
      if (c == null) return '';
      const n = Number(c);
      return `$${n.toFixed(n < 1 ? 3 : 2)}`;
    },
    statusKey() {
      if (this.interaction) return this.interaction.status || 'unknown';
      const a = this.legacyAnalysing;
      if (a === true || a === 'pending') return 'running';
      if (a === false || a === 'done') return 'done';
      return 'unknown';
    },
    isStuck() {
      return this.statusKey === 'failed' || this.statusKey === 'queued';
    },
    statusLabel() {
      return {
        queued: 'En file', running: 'En cours', done: 'Terminé',
        failed: 'Échec', unknown: 'Inconnu',
      }[this.statusKey] || this.statusKey;
    },
    statusClass() {
      return 'st-' + this.statusKey;
    },
  },
  methods: {
    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.interaction-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border: 1px solid #ececf1;
  border-radius: 10px;
  margin-bottom: 0.5rem;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.interaction-card:hover {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}
.interaction-card.is-followup {
  border-left: 3px solid #a78bfa;
  margin-left: 1.25rem;
}
.interaction-card.is-stuck {
  border-color: #f3b4b4;
  background: #fff7f7;
}
.ic-main { flex: 1; min-width: 0; }
.ic-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
}
.ic-name {
  font-weight: 500;
  font-size: 0.82rem;
  color: #6b7180;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.ic-time { color: #9095a3; font-size: 0.78rem; white-space: nowrap; }
.ic-email { color: #4a4f5c; font-size: 0.95rem; font-weight: 600; margin-top: 0.2rem; }
.ic-meta {
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: #565c6b;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.ic-metric { font-variant-numeric: tabular-nums; }
.ic-sep { color: #c3c7d1; }
.ic-cost { color: #2e7d52; font-weight: 600; }
.ic-tools { color: #7a7f8c; font-style: italic; }

.ic-type-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  white-space: nowrap;
}
.type-flash { background: #e3edff; color: #2257c9; }
.type-agent { background: #ece6ff; color: #5b34c9; }
.type-deep  { background: #d9f0ff; color: #0b6e9e; }
.type-unknown { background: #eee; color: #777; }

.ic-followup-tag {
  font-size: 0.72rem;
  color: #7c4dff;
  background: #f2edff;
  padding: 0.08rem 0.4rem;
  border-radius: 6px;
}

.ic-status-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;
}
.st-done { background: #e3f6ec; color: #1c7a45; }
.st-running { background: #fff3da; color: #946200; }
.st-queued { background: #ffe9e0; color: #b4502a; }
.st-failed { background: #fde2e2; color: #b32424; }
.st-unknown { background: #eee; color: #777; }
</style>
